<template>
  <Teleport
    v-if="teleport"
    :to="teleport"
  >
    <Transition
      :name="animation ? 'popup-overlay' : null"
      :appear="transitionAppear"
      @after-leave="$emit('closed')"
    >
      <div
        v-show="show"
        class="popup-overlay"
        :class="overlayClass"
        @click="onOverlayClick"
      />
    </Transition>
    <Transition
      :name="animation ? `popup-slide-${position}` : null"
      :appear="transitionAppear"
    >
      <div
        v-show="show"
        class="popup"
        :class="[
          `popup--${position}`
        ]"
        v-bind="$attrs"
      >
        <slot />
      </div>
    </Transition>
  </Teleport>
  <template v-else>
    <Transition
      :name="animation ? 'popup-overlay' : null"
      :appear="transitionAppear"
    >
      <div
        v-show="show"
        class="popup-overlay"
      />
    </Transition>
    <Transition
      :name="animation ? `popup-slide-${position}` : null"
      :appear="transitionAppear"
    >
      <div
        v-show="show"
        class="popup"
        :class="[
          `popup--${position}`
        ]"
        v-bind="$attrs"
      >
        <slot />
      </div>
    </Transition>
  </template>
</template>

<script setup>
import { useScrollLock } from '@vueuse/core'
import { useThemeColor } from '@/hooks/useThemeColor'

const props = defineProps({
  /**
   * 如果设置为 null 则没有渲染动画
   * 'bottom' | 'center' | null
   * @default 'center'
   */
  position: {
    type: String,
    default: 'center'
  },
  teleport: {
    type: String,
    default: null
  },
  overlayClass: {
    type: [String, Array, Object],
    default: null
  },
  closeOnClickOverlay: {
    type: Boolean,
    default: true
  },
  transitionAppear: Boolean,
  animation: {
    type: Boolean,
    default: true
  }
})
defineOptions({
  inheritAttrs: false
})

const emit = defineEmits(['clickOverlay', 'closed'])

const show = defineModel('show', { type: Boolean })

const { set, reset } = useThemeColor()

const onOverlayClick = () => {
  emit('clickOverlay')
  if (props.closeOnClickOverlay) show.value = false
}

const isLocked = useScrollLock(() => document.body)

watch(show, (val) => {
  isLocked.value = val
  if (val) set('#141414')
  else reset()
}, {
  immediate: true
})
</script>

<style lang="less">
.popup {
  &-overlay {
    z-index: 1200;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.64);
    backdrop-filter: blur(8px);
  }

  z-index: 1200;
  position: fixed;

  &--center {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &--bottom {
    bottom: 0;
    width: 100%;
  }
}

.popup-overlay-enter-active,
.popup-overlay-leave-active {
  transition: opacity 0.3s ease;
  transition-delay: 0.1s;
}
.popup-overlay-enter-from,
.popup-overlay-leave-to {
  opacity: 0;
}

/** Popup position center */
.popup-slide-center-enter-active {
  animation: bounce-in 0.2s ease-in;
}

.popup-slide-center-leave-active {
  animation: bounce-in 0.2s reverse;
}

@keyframes bounce-in {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

/** Popup position bottom */
.popup-slide-bottom-enter-active,
.popup-slide-bottom-leave-active {
  transition: transform 0.3s ease-out, opacity 0.3s ease;
}

.popup-slide-bottom-enter-from {
  transform: translateY(100%);
  opacity: 0;
}
.popup-slide-bottom-enter-to {
  transform: translateY(0);
  opacity: 1;
}

.popup-slide-bottom-leave-from {
  transform: translateY(0);
  opacity: 1;
}
.popup-slide-bottom-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
